import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import {ProductAnswers} from "./ProductInfoPage";
import PublicNav, { PublicFooter } from "../components/PublicNav";
const samples = [
  ["AM", "Alex M.", "Head of Operations", "Northstar Studio", "Austin, US"],
  ["JL", "Jordan L.", "Founder", "Fieldwork", "London, UK"],
  ["SK", "Sam K.", "Marketing Director", "Brightpath", "Toronto, CA"],
  ["TC", "Taylor C.", "VP of Sales", "Forma Labs", "New York, US"],
];
export default function LandingPage() {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  return (
    <div className="public-site">
      <PublicNav />
      <main>
        <section className="landing-hero">
          <div className="hero-orbit" />
          <div className="hero-copy">
            <span className="eyebrow">
              <span className="live-dot" /> FIND YOUR NEXT OPPORTUNITY
            </span>
            <h1>
              Great connections.
              <br />
              New possibilities.
              <br />
              <em>Your next customer.</em>
            </h1>
            <p>
              Find the people behind your next opportunity. Search business
              contacts, build your shortlist, and unlock the details when you're
              ready.
            </p>
            <form
              className="hero-search"
              onSubmit={(e) => {
                e.preventDefault();
                navigate(`/explore?keyword=${encodeURIComponent(query)}`);
              }}
            >
              <span aria-hidden="true">⌕</span>
              <input
                aria-label="Search business contacts"
                placeholder="Try a company, role, or industry…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button className="button button-dark" type="submit">
                Explore leads <span>→</span>
              </button>
            </form>
            <div className="hero-assurances">
              <span>✓ Preview before you buy</span>
              <span>✓ 1 credit per contact</span>
              <span>✓ Export on your terms</span>
            </div>
            <div className="hero-note">
              <span className="stacked-avatars">
                <i>A</i>
                <i>J</i>
                <i>S</i>
              </span>
              <p>
                Built for founders, agencies,
                <br />
                <strong>and teams making their next move.</strong>
              </p>
            </div>
          </div>
          <div className="hero-product">
            <div className="floating-label">
              <span>✦</span> Less searching. More opportunity.
            </div>
            <div className="product-preview">
              <div className="preview-top">
                <span className="preview-icon">◈</span>
                <strong>Your prospect shortlist</strong>
                <span className="preview-dots">•••</span>
              </div>
              <div className="preview-filter-row">
                <span>People</span>
                <span>Company</span>
                <span>Location</span>
                <span className="selected-filter">+ Add filter</span>
              </div>
              <div className="preview-table">
                <div className="preview-table-head">
                  <span>CONTACT</span>
                  <span>COMPANY</span>
                  <span>DETAILS</span>
                </div>
                {samples.map(([initials, name, role, company, location], i) => (
                  <div className="preview-contact" key={name}>
                    <span className={`preview-avatar tone-${i}`}>
                      {initials}
                    </span>
                    <div>
                      <strong>{name}</strong>
                      <small>{role}</small>
                    </div>
                    <div>
                      <strong>{company}</strong>
                      <small>{location}</small>
                    </div>
                    <span className="locked-chip">⌑ Locked</span>
                  </div>
                ))}
              </div>
              <div className="preview-bottom">
                <span>Illustrative interface preview</span>
                <Link to="/explore">Try a real search ↗</Link>
              </div>
            </div>
            <div className="floating-credit">
              <span className="mint-icon">✦</span>
              <div>
                <strong>More room to connect</strong>
                <small>5,000 monthly · 60,000 annually</small>
              </div>
            </div>
          </div>
        </section>
        <section className="benefit-strip">
          <span>YOUR WORKFLOW, SIMPLIFIED</span>
          <strong>Find the right people</strong>
          <i>→</i>
          <strong>Unlock the details</strong>
          <i>→</i>
          <strong>Make your next move</strong>
        </section>
        <section className="public-section" id="how-it-works">
          <div className="section-heading">
            <span className="eyebrow">FROM SEARCH TO SHORTLIST</span>
            <h2>
              A simpler way
              <br />
              to start the conversation.
            </h2>
            <p>
              Everything you need to turn a target market into a useful contact
              list.
            </p>
          </div>
          <div className="how-grid">
            {[
              [
                "01",
                "Find your people",
                "Narrow your search by role, industry, company size, and location. See matching contacts before unlocking.",
                "⌕",
              ],
              [
                "02",
                "Unlock with confidence",
                "See available fields and the data status first. One credit unlocks all available contact details.",
                "⌑",
              ],
              [
                "03",
                "Take the next step",
                "Organize your contacts into lists, keep private notes, and export a CSV for your existing workflow.",
                "↗",
              ],
            ].map(([n, title, body, icon]) => (
              <article className="how-card" key={n}>
                <div>
                  <span className="how-icon">{icon}</span>
                  <span className="step-number">{n}</span>
                </div>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </section>
        <section className="feature-band">
          <div className="public-section feature-split">
            <div>
              <span className="eyebrow">A WORKSPACE THAT WORKS FOR YOU</span>
              <h2>
                Your best prospects.
                <br />
                All in one place.
              </h2>
              <p>
                Spend less time moving between spreadsheets. Keep your searches,
                shortlists, and unlocked contacts together.
              </p>
              <Link className="button button-dark" to="/signup">
                Build your first list ↗
              </Link>
            </div>
            <div className="feature-tiles">
              <article>
                <span>▤</span>
                <h3>Lists that stay organized</h3>
                <p>
                  Separate industries, campaigns, or clients into named lists.
                </p>
              </article>
              <article>
                <span>☆</span>
                <h3>Save a good search</h3>
                <p>Return to your targeting without rebuilding every filter.</p>
              </article>
              <article>
                <span>↓</span>
                <h3>Your contacts, ready to go</h3>
                <p>Export unlocked contacts without spending more credits.</p>
              </article>
              <article>
                <span>◉</span>
                <h3>Know what's available</h3>
                <p>Check email and phone availability before an unlock.</p>
              </article>
            </div>
          </div>
        </section>
        <section className="public-section home-pricing">
          <div>
            <span className="eyebrow">MORE CONTACTS. SIMPLE PRICING.</span>
            <h2>
              Make room for
              <br />
              your next big opportunity.
            </h2>
            <p>Monthly flexibility or a full year of prospecting.</p>
            <Link to="/pricing">Compare the two plans →</Link>
          </div>
          <div className="home-plan">
            <span>MONTHLY</span>
            <strong>
              $40<small>/mo</small>
            </strong>
            <p>5,000 credits every month</p>
            <Link to="/pricing">Choose monthly ↗</Link>
          </div>
          <div className="home-plan annual">
            <span>ANNUAL</span>
            <strong>
              $240<small>/year</small>
            </strong>
            <p>60,000 credits upfront</p>
            <span className="offer-badge">Up to 50% off your first year</span>
            <Link to="/pricing">See annual offer ↗</Link>
          </div>
        </section>
        <ProductAnswers compact />
        <section className="cta-band">
          <span className="eyebrow">YOUR NEXT CONNECTION IS OUT THERE</span>
          <h2>Let's find it.</h2>
          <p>Explore the database. Build a shortlist. Take your next step.</p>
          <Link className="button button-mint" to="/explore">
            Start exploring <span>↗</span>
          </Link>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
